import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { FaCar, FaPercentage, FaCalendarAlt, FaDollarSign, FaShieldAlt } from 'react-icons/fa';

const CustomerLiveView = () => {
  const { token } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // In a real app, this would fetch from /api/d2-deals/live/:token
    // and listen for WebSocket events on the same token.
    setTimeout(() => {
      setDeal({
        vehicle: {
          make: 'Toyota',
          model: 'Camry XSE',
          year: 2024,
          salePrice: 28500
        },
        finance: {
          apr: 8.0,
          term: 60
        },
        calculation: {
          totalAmountFinanced: 30000,
          monthlyPayment: 608.29
        }
      });
      setLoading(false);
    }, 1000);
  }, [token]);

  if (loading) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-950 text-slate-200">
        <div className="flex flex-col items-center gap-3">
          <span className="w-10 h-10 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin"></span>
          <p className="text-slate-400 text-sm font-medium tracking-wide">Establishing Secure Connection...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 text-white font-sans flex flex-col justify-center items-center p-4 animate-fade-in-up selection:bg-indigo-500 selection:text-white">
      {/* Background glow effects */}
      <div className="fixed -top-40 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-indigo-500/10 rounded-full blur-[120px] pointer-events-none -z-10"></div>
      <div className="fixed -bottom-40 left-1/2 -translate-x-1/2 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-[120px] pointer-events-none -z-10"></div>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-indigo-500/10 border border-indigo-500/20 text-indigo-400 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg shadow-indigo-500/5 animate-pulse">
            <FaShieldAlt size={22} />
          </div>
          <h1 className="text-2xl font-black text-white tracking-tight">Your Deal Snapshot</h1>
          <p className="text-slate-400 text-xs mt-1.5 font-medium">Real-time updates secured by your sales consultant</p>
        </div>

        {/* Live Card Container */}
        <div className="bg-slate-900/80 backdrop-blur-md border border-slate-800/80 rounded-3xl p-6 shadow-2xl space-y-6 relative overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-indigo-500 to-transparent"></div>
          
          <div className="flex justify-between items-center border-b border-slate-800/80 pb-4">
            <div className="flex items-center gap-3">
              <span className="p-2.5 bg-slate-800/80 text-indigo-400 rounded-xl border border-slate-700/50">
                <FaCar size={16} />
              </span>
              <div>
                <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Vehicle Selected</p>
                <h2 className="text-base font-extrabold text-white mt-0.5">{deal.vehicle.year} {deal.vehicle.make} {deal.vehicle.model}</h2>
              </div>
            </div>
            <div className="text-right">
              <p className="text-slate-400 text-[10px] font-bold uppercase tracking-wider">Sale Price</p>
              <p className="text-base font-extrabold text-white mt-0.5">${deal.vehicle.salePrice.toLocaleString()}</p>
            </div>
          </div>
          
          <div className="space-y-3.5 text-sm text-slate-350">
            <div className="flex justify-between items-center">
              <span className="text-slate-400 flex items-center gap-1.5"><FaPercentage size={12} className="text-slate-500" /> Lender APR</span>
              <span className="font-bold text-white">{deal.finance.apr}%</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-slate-400 flex items-center gap-1.5"><FaCalendarAlt size={12} className="text-slate-500" /> Term Duration</span>
              <span className="font-bold text-white">{deal.finance.term} Months</span>
            </div>
            <div className="flex justify-between items-center pt-2 border-t border-slate-800/50">
              <span className="text-slate-400 flex items-center gap-1.5"><FaDollarSign size={12} className="text-slate-500" /> Amount Financed</span>
              <span className="font-bold text-indigo-400">${deal.calculation.totalAmountFinanced.toLocaleString()}</span>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-600 to-blue-600 rounded-2xl p-5 mt-6 flex justify-between items-center shadow-lg shadow-indigo-600/10">
            <div>
              <span className="text-indigo-100 text-[10px] font-bold uppercase tracking-wider block">Estimated Monthly</span>
              <span className="text-indigo-100 text-xs font-semibold">Payment</span>
            </div>
            <span className="text-3xl font-black tracking-tight text-white">${deal.calculation.monthlyPayment}</span>
          </div>
        </div>
        
        <p className="text-center text-[10px] text-slate-500 mt-6 leading-relaxed">
          🔒 Secure read-only link. Parameter updates by your sales consultant will show instantly.
        </p>
      </div>
    </div>
  );
};

export default CustomerLiveView;
